import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { isEmptyOrInvalidString, uuid } from 'src/utilities/text';
import { t } from 'src/i18n';

import 'src/library/elements/d-smooth-resize';
import 'src/library/elements/d-section';
import 'src/library/fields/d-view-info';
import 'src/library/editors/elements/d-select-checkbox';
import 'src/library/editors/elements/d-edit-text';
import 'src/library/editors/elements/d-select-radio';
import type { TopicItemViewModel, TopicViewModel } from '@trinnvis/dabih-api-client';
import type { AddToCollectionDialogInput } from './add-to-collection-dialog-input';
import type { AddToCollectionDialogResult } from './add-to-collection-dialog-result';
import type { Option } from '../library/editors/elements/option';
import type { SelectDropdownOption } from '../library/editors/elements/select-dropdown-option';
import type { DialogActionInput } from '../library/components/DialogActionInput';

@customElement('add-to-collection-dialog')
export class AddToCollectionDialog extends BaseDialog<AddToCollectionDialogInput, AddToCollectionDialogResult> {
  static readonly styles = BaseDialog.styles.concat(css`
    d-select-checkbox {
      margin-top: -6px;
    }
    .new-collection {
      display: flex;
      margin-top: 8px;
    }
    .new-collection > div {
      width: 27px;
      height: 30px;
      background: url(/images/check-mini-gray.svg) -4px 2px no-repeat;
      background-size: 24px;
      opacity: 0.5;
      cursor: pointer;
    }
    .new-collection > div[disabled] {
      cursor: default;
    }
    .new-collection > div[selected] {
      background: url(/images/check-mini-blue.svg) -4px 2px no-repeat;
      background-size: 24px;
      opacity: 1;
    }
    d-edit-text {
      flex: 1;
    }
    d-select-radio {
      box-shadow: none;
      margin-top: -12px;
    }
  `);
  @property({ type: Boolean })
  show = true;
  @property({ type: String })
  entityUuid = '';
  @property({ type: String })
  entityType = '';
  @property({ type: Array })
  oldCollections: TopicViewModel[] = [];
  @property({ type: Array })
  collections: TopicViewModel[] = [];
  @property({ type: String })
  recurringEventType = '';
  @property({ type: Array })
  occurrenceUuids: string[] = [];
  @property({ type: Boolean })
  allOcurrences = true;
  @property({ type: Number })
  width = 500;
  @state()
  newCollectionName = '';
  @state()
  newCollectionSelected = false;

  private get info() {
    return t('content.addToCollectionInfo');
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('content.cancel'), action: 'cancel' },
      {
        name: t('content.done'),
        action: 'done',
        main: true,
        disabled: this.collections.length === 0 && isEmptyOrInvalidString(this.newCollectionName),
      },
    ];
  }

  protected get calculatedTitle(): string {
    return t('content.addToCollectionTitle');
  }

  private get recurringEventInfo() {
    if (this.recurringEventType === 'eventOccurrences') {
      return t('content.addToCollectionRecurringTaskInfo');
    }
    if (this.recurringEventType === 'meetingOccurrences') {
      return t('content.addToCollectionRecurringMeetingInfo');
    }
    return '';
  }

  private get recurringEventOptions(): Option[] {
    if (this.recurringEventType === 'eventOccurrences') {
      return [
        { value: 'true', text: t('content.addToCollectionAllRecurrences') },
        { value: '', text: t('content.addToCollectionOnlyThisTask') },
      ];
    }
    if (this.recurringEventType === 'meetingOccurrences') {
      return [
        { value: 'true', text: t('content.addToCollectionWholeMeetingSeries') },
        { value: '', text: t('content.addToCollectionOnlyThisMeeting') },
      ];
    }
    return [];
  }

  private get allOcurrencesValue() {
    return this.allOcurrences ? 'true' : '';
  }

  private get collectionsOptions(): SelectDropdownOption[] {
    return this.collections.map((c) => {
      return { value: c.uuid, text: c.name };
    });
  }

  private get relatedCollections() {
    const result: string[] = [];
    this.collections.forEach((c) => {
      c.items.forEach((i) => {
        if (i.uuid === this.entityUuid) {
          result.push(c.uuid);
        }
      });
    });
    return result;
  }

  renderBody() {
    return html`
      <d-smooth-resize>
        <d-section>
          <d-view-info .content=${this.info}></d-view-info>
          <div class="fullWidth">
            ${this.collections.length > 0
              ? html` <d-select-checkbox
                  class="fullWidth"
                  checklist
                  .options=${this.collectionsOptions}
                  .value=${this.relatedCollections}
                  @value-changed=${(e) => this.onSelectedCollectionsChanged(e)}
                ></d-select-checkbox>`
              : nothing}
            <div class="new-collection">
              ${this.collections.length > 0
                ? html` <div
                    ?disabled="${isEmptyOrInvalidString(this.newCollectionName)}"
                    ?selected="${this.newCollectionSelected}"
                    @click=${() => {
                      if (this.newCollectionSelected) {
                        this.newCollectionSelected = false;
                        this.newCollectionName = '';
                      }
                    }}
                  ></div>`
                : nothing}
              <d-edit-text
                system-content
                class="fullWidth"
                placeholder=${t('content.addToCollectionNewCollection')}
                .autofocus=${this.collections.length === 0}
                .value=${this.newCollectionName}
                @value-changed=${(e) => this.onNewCollectionNameChanged(e)}
              ></d-edit-text>
            </div>
          </div>
          ${this.recurringEventType
            ? html` <d-view-info .content=${this.recurringEventInfo}></d-view-info
                ><d-select-radio
                  class="fullWidth"
                  .options=${this.recurringEventOptions}
                  .value=${this.allOcurrencesValue}
                  @value-changed=${(e) => this.onAllOccurrenvesChanged(e)}
                ></d-select-radio>`
            : nothing}
        </d-section>
      </d-smooth-resize>
    `;
  }

  arraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  }

  private get changedCollections() {
    const result: TopicViewModel[] = [];
    this.collections.forEach((collection) => {
      const oldCollection = this.oldCollections.find((oc) => {
        return oc.uuid === collection.uuid;
      });
      if (oldCollection) {
        const newItems = collection.items
          .map((item) => {
            return item.uuid;
          })
          .sort();
        const oldItems = oldCollection.items
          .map((item) => {
            return item.uuid;
          })
          .sort();
        if (!this.arraysEqual(newItems, oldItems)) {
          result.push(collection);
        }
      } else {
        result.push(collection);
      }
    });
    return result;
  }

  private get newCollection(): TopicViewModel | undefined {
    if (this.newCollectionSelected && this.newCollectionName !== '') {
      let items: TopicItemViewModel[] = [{ uuid: this.entityUuid, type: this.entityType }];
      if (this.allOcurrences && this.occurrenceUuids.length) {
        items = this.occurrenceUuids.map((uuid) => {
          return { uuid, type: this.entityType };
        });
      }
      return {
        uuid: uuid(),
        name: this.newCollectionName,
        description: '',
        hasDraft: false,
        isConfirmedEntity: true,
        deleted: false,
        pages: [0],
        items,
      };
    }
    return undefined;
  }

  protected fetchResult(detail: string | undefined): AddToCollectionDialogResult {
    if (detail === 'done') {
      return {
        action: 'done',
        collections: this.changedCollections,
        newCollection: this.newCollection,
      };
    } else {
      return {
        action: 'cancel',
      };
    }
  }

  protected initializeDialog(input: AddToCollectionDialogInput) {
    this.entityUuid = input.entityUuid;
    this.entityType = input.entityType;
    this.oldCollections = input.collections;
    this.collections = input.collections;
    this.occurrenceUuids = input.occurrenceUuids;
    this.recurringEventType = input.recurringEventType;
  }

  private onAllOccurrenvesChanged(e) {
    this.allOcurrences = e.detail.value === 'true';
  }

  private updateCollection(selectedCollectionUuids: string[], uuids: string[]) {
    this.collections = this.collections.map((collection) => {
      let items = [...collection.items];
      if (selectedCollectionUuids.includes(collection.uuid)) {
        let exists = false;
        collection.items.forEach((item) => {
          if (item.uuid === this.entityUuid) {
            exists = true;
          }
        });
        if (!exists) {
          uuids.forEach((uuid) => {
            items.push({ uuid, type: this.entityType });
          });
        }
      } else {
        items = items.filter((item) => {
          return !uuids.includes(item.uuid);
        });
      }
      return { ...collection, items };
    });
  }

  private onSelectedCollectionsChanged(e) {
    e.preventDefault();
    let uuids: string[] = [this.entityUuid];
    if (this.allOcurrences && this.occurrenceUuids.length) {
      uuids = this.occurrenceUuids;
    }
    this.updateCollection(e.detail.value, uuids);
  }

  private onNewCollectionNameChanged(e) {
    e.stopPropagation();
    this.newCollectionName = e.detail.value;
    this.newCollectionSelected = !isEmptyOrInvalidString(this.newCollectionName);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'add-to-collection-dialog': AddToCollectionDialog;
  }
}
